import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { listCounterpartiesApi } from "../api/financeiroApi";
import type { FinCounterparty } from "../api/finTypes";
import CounterpartyFormModal from "./CounterpartyFormModal";

type Props = {
  token: string;
  id: string;
  value: { id: string; name: string } | null;
  onChange: (value: { id: string; name: string } | null) => void;
  disabled?: boolean;
  /** Mostra o atalho "Novo" (quem só lê não cadastra). */
  canCreate?: boolean;
};

/**
 * Escolha de fornecedor/cliente com busca no servidor (a lista pode ser longa) e
 * atalho para cadastrar sem sair do formulário.
 */
export default function CounterpartyPicker({ token, id, value, onChange, disabled, canCreate }: Props) {
  const [q, setQ] = useState("");
  const [options, setOptions] = useState<FinCounterparty[]>([]);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!token) return;
    const handle = setTimeout(() => {
      listCounterpartiesApi(token, { q: q.trim() || undefined, limit: 30 })
        .then((data) => setOptions(data.items))
        .catch(() => setOptions([]));
    }, 250);
    return () => clearTimeout(handle);
  }, [token, q]);

  // O escolhido continua na lista mesmo quando a busca não o traz.
  const list = value && !options.some((o) => o.id === value.id) ? [{ id: value.id, name: value.name }, ...options] : options;

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <input
          className="dpInput"
          placeholder="Buscar por nome ou CPF/CNPJ"
          aria-label="Buscar fornecedor ou cliente"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          disabled={disabled}
        />
        <div style={{ display: "flex", gap: 6 }}>
          <select
            id={id}
            className="dpSelect"
            style={{ flex: 1, minWidth: 0 }}
            value={value?.id ?? ""}
            onChange={(e) => {
              const found = list.find((o) => o.id === e.target.value);
              onChange(found ? { id: found.id, name: found.name } : null);
            }}
            disabled={disabled}
          >
            <option value="">Nenhum</option>
            {list.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
          {canCreate ? (
            <button
              type="button"
              className="dpBtn dpBtn--secondary dpBtn--small"
              onClick={() => setCreating(true)}
              disabled={disabled}
            >
              Novo
            </button>
          ) : null}
        </div>
      </div>

      {/* Portal: o modal tem <form> próprio e não pode ficar dentro do <form> do lançamento. */}
      {creating
        ? createPortal(
            <CounterpartyFormModal
              token={token}
              onClose={() => setCreating(false)}
              onSaved={(saved) => {
                setCreating(false);
                setOptions((prev) => [saved, ...prev]);
                onChange({ id: saved.id, name: saved.name });
              }}
            />,
            document.body
          )
        : null}
    </>
  );
}
